//
// photos.ts — a picture of the actual aircraft
//
// Translation of `Networking/PlanespottersService.swift`.
//
// ADS-B gives a hex address and usually a tail number; the Planespotters
// public photo API turns either into a picture of *that specific airframe*
// — not a stock photo of the type. That is what makes the detail panel feel
// like tracking something rather than reading a table.
//
// Free, no key, with two conditions:
//
//   1. A descriptive User-Agent is mandatory (a generic library string gets
//      a 403). A browser cannot set that header, which is why this route is
//      proxied — see api/config.ts.
//   2. **Attribution is required.** Every photo must be shown with the
//      photographer's name and a link to its Planespotters page. Both come
//      back in the response and both are carried on `TrackedObject`, so the
//      UI cannot quietly drop them.
//
// Rate limit is roughly one request a second, so lookups happen on
// selection only — never for the hundreds of aircraft on the map at once.
//

import { PHOTOS_BASE, fetchJSON } from "./config";

export interface AircraftPhoto {
  /** ~420x280 — the size the detail panel wants. */
  imageUrl: string;
  thumbnailUrl: string;
  /** Required attribution link. */
  pageUrl: string;
  /** Required credit. */
  photographer: string;
}

interface RawResponse {
  photos?: Array<{
    thumbnail?: { src: string };
    thumbnail_large?: { src: string };
    link?: string;
    photographer?: string;
  }>;
}

/**
 * Photographs never change for a given airframe, so a hit is permanent for
 * the session. Misses are cached too — most smaller aircraft have no photo,
 * and re-asking for each of them on every selection would burn the rate
 * limit for nothing.
 */
const cache = new Map<string, AircraftPhoto | null>();

async function lookup(path: "reg" | "hex", value: string): Promise<AircraftPhoto | null> {
  const key = `${path}:${value.toLowerCase()}`;
  if (cache.has(key)) return cache.get(key) ?? null;

  const response = await fetchJSON<RawResponse>(
    `${PHOTOS_BASE}/${path}/${encodeURIComponent(value)}`,
    12000,
  );
  const entry = response?.photos?.[0];
  const large = entry?.thumbnail_large?.src ?? entry?.thumbnail?.src;

  if (!entry || !large) {
    cache.set(key, null);
    return null;
  }

  const photo: AircraftPhoto = {
    imageUrl: large,
    thumbnailUrl: entry.thumbnail?.src ?? large,
    pageUrl: entry.link ?? "https://www.planespotters.net",
    photographer: entry.photographer ?? "Unknown photographer",
  };
  cache.set(key, photo);
  return photo;
}

/**
 * Best available photo, preferring the registration (Planespotters indexes
 * by tail number first) and falling back to the ICAO hex address.
 */
export async function fetchPhoto(
  registration?: string,
  hex?: string,
): Promise<AircraftPhoto | null> {
  const reg = registration?.trim();
  if (reg) {
    const found = await lookup("reg", reg);
    if (found) return found;
  }
  const h = hex?.trim();
  if (h) return lookup("hex", h);
  return null;
}
